import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAX_COLUMN_WIDTH } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { Column, GridApi, SortChangedEvent } from 'ag-grid-community';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataGridHelperService {
  readonly maxColumnWidth = MAX_COLUMN_WIDTH;
  translate: TranslateService = inject(TranslateService);
  subscribeToObservable<T>(observable: Observable<T>, callback: (value: T) => void, destroyRef: DestroyRef): void {
    observable.pipe(takeUntilDestroyed(destroyRef)).subscribe(callback);
  }

  extractSortColumns(event: SortChangedEvent): { field: string; sort: string } | null {
    const columns = event.columns?.filter(col => col.getSort());
    if (columns?.length) {
      const firstColumn = columns[0];
      const sortColumns = firstColumn.getColDef().context?.columns as string[];
      return {
        field: sortColumns?.[0] || firstColumn.getColDef().field!,
        sort: firstColumn.getSort()!,
      };
    }
    return null;
  }

  getNoRowsTemplate(hasFilters = false, hasSearch = false): string {
    const icon = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M30.1944 30.1944C30.7314 29.6574 31.602 29.6574 32.1389 30.1944L39.4723 37.5277C40.0092 38.0647 40.0092 38.9353 39.4723 39.4723C38.9353 40.0092 38.0647 40.0092 37.5277 39.4723L30.1944 32.1389C29.6574 31.602 29.6574 30.7314 30.1944 30.1944Z" fill="#656579"/>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M20.1667 6.875C12.8259 6.875 6.875 12.8259 6.875 20.1667C6.875 27.5075 12.8259 33.4583 20.1667 33.4583C23.8438 33.4583 27.17 31.967 29.5778 29.5528C31.9773 27.147 33.4583 23.8311 33.4583 20.1667C33.4583 12.8259 27.5075 6.875 20.1667 6.875ZM4.125 20.1667C4.125 11.3071 11.3071 4.125 20.1667 4.125C29.0262 4.125 36.2083 11.3071 36.2083 20.1667C36.2083 24.5884 34.4175 28.5945 31.5249 31.4947C28.6223 34.4051 24.6038 36.2083 20.1667 36.2083C11.3071 36.2083 4.125 29.0262 4.125 20.1667Z" fill="#656579"/>
                  </svg>`;
    const title = `<h4 class="no-rows-title">${this.translate.instant('commonGrid.no-rows-title')}</h4>`;
    const showSuggestion = hasFilters || hasSearch;
    const suggestion = showSuggestion
      ? `<p class="no-rows-suggestion">${this.translate.instant('commonGrid.no-rows-suggestion')}</p>`
      : '';
    return `<div class="no-rows-template">
              ${icon}
              <br>
              <div>
                ${title}${suggestion}
              </div>
            </div>`;
  }

  handleColumnWidths(gridApi: GridApi | null): void {
    // find columns without manually set width
    const autosizableColumns: Column[] = [];
    gridApi?.getColumns()?.forEach(column => {
      const colDef = column.getColDef();

      if (!('width' in colDef && colDef.width)) {
        autosizableColumns.push(column);
      }
    });

    // autosize columns without manually set width
    gridApi?.autoSizeColumns(autosizableColumns);

    // restrict max width
    const columnsWithWidthLimit = gridApi
      ?.getColumns()
      ?.filter((column: Column) => column.getActualWidth() > MAX_COLUMN_WIDTH)
      .map((column: Column) => ({ key: column, newWidth: MAX_COLUMN_WIDTH }));
    gridApi?.setColumnWidths(columnsWithWidthLimit as { key: Column; newWidth: number }[]);
  }
}
